// ── reading an innernet archive back, in the browser ─────────────────────────
//
// The other half of the exit (lib/export/archive writes it): a person drops the
// zip they took out into "bring your memory in", and it is unpacked here, on
// their own machine. Nothing is sent until they say so, and then only:
//   · each map's project.json, in pieces small enough for one request
//     (POST /api/account/restore → the same save every map write goes through);
//   · the facts in you/facts/*.md, as import sources (/api/self/import-web), so
//     netti reads them again and the person's own words come back as theirs.
// Pure but for the zip itself; the splitting is tested on its own.

import { ARCHIVE_FORMAT } from './archive'

export interface ArchiveMap {
  slug: string
  name: string
  pages: number
  cells: number
  /** the POST /v1/projects body, as the archive holds it */
  body: RestoreBody
}

export interface RestoreBody {
  name: string
  slug: string
  type?: string
  tagline?: string | null
  commit_message?: string
  dimensions: Record<string, string>
  nodes: Record<string, { type: string; content: unknown }>
}

export interface ArchiveSource {
  path: string
  label: string
  content: string
  size: number
  kind: string
}

export interface ReadArchive {
  kind: 'innernet'
  exportedAt: string
  maps: ArchiveMap[]
  /** you/facts/*.md as import sources */
  facts: ArchiveSource[]
  /** how many current facts those files hold */
  factCount: number
}

/** A request body stays under this (Vercel takes 4.5 MB; json escapes grow text). */
export const RESTORE_PIECE_CHARS = 1_000_000

/** Is this zip an innernet archive? Reads only its manifest. */
export async function readArchiveZip(data: ArrayBuffer): Promise<ReadArchive | null> {
  const JSZip = (await import('jszip')).default
  const zip = await JSZip.loadAsync(data)
  const manifestEntry = Object.values(zip.files).find((f) => !f.dir && /^(?:[^/]+\/)?manifest\.json$/.test(f.name))
  if (!manifestEntry) return null
  let manifest: { format?: string; exported_at?: string }
  try {
    manifest = JSON.parse(await manifestEntry.async('string'))
  } catch {
    return null
  }
  if (manifest.format !== ARCHIVE_FORMAT) return null
  const root = manifestEntry.name.slice(0, manifestEntry.name.length - 'manifest.json'.length)

  const maps: ArchiveMap[] = []
  const facts: ArchiveSource[] = []
  let factCount = 0
  for (const f of Object.values(zip.files)) {
    if (f.dir || !f.name.startsWith(root)) continue
    const rel = f.name.slice(root.length)
    if (/^projects\/[^/]+\/project\.json$/.test(rel)) {
      try {
        const body = JSON.parse(await f.async('string')) as RestoreBody
        if (!body || typeof body !== 'object' || !body.slug) continue
        const dimensions = body.dimensions && typeof body.dimensions === 'object' ? body.dimensions : {}
        const nodes = body.nodes && typeof body.nodes === 'object' ? body.nodes : {}
        maps.push({
          slug: String(body.slug),
          name: String(body.name ?? body.slug),
          pages: Object.keys(dimensions).length,
          cells: Object.keys(nodes).length,
          body: { ...body, dimensions, nodes },
        })
      } catch {
        /* a map that will not parse is left out, and the count says so */
      }
    } else if (/^you\/facts\/[^/]+\.md$/.test(rel)) {
      const content = await f.async('string')
      factCount += (content.match(/^- /gm) ?? []).length
      facts.push({ path: `archive://${rel}`, label: `innernet archive: ${rel.slice('you/facts/'.length, -3)}`, content, size: content.length, kind: 'innernet-archive' })
    }
  }
  maps.sort((a, b) => a.name.localeCompare(b.name))
  facts.sort((a, b) => a.path.localeCompare(b.path))
  return { kind: 'innernet', exportedAt: String(manifest.exported_at ?? ''), maps, facts, factCount }
}

/**
 * One map as request bodies each under `limit` characters of json: the first
 * names it (and creates it), every piece carries the same name and slug, and
 * pages and cells are shared out in order. A single page larger than the limit
 * goes alone (the server says if it is too big).
 */
export function restorePieces(body: RestoreBody, limit = RESTORE_PIECE_CHARS): RestoreBody[] {
  const head = { name: body.name, slug: body.slug, type: body.type, tagline: body.tagline ?? null, commit_message: body.commit_message }
  const pieces: RestoreBody[] = []
  let cur: RestoreBody = { ...head, dimensions: {}, nodes: {} }
  let size = JSON.stringify(cur).length
  const fresh = () => {
    if (Object.keys(cur.dimensions).length || Object.keys(cur.nodes).length) pieces.push(cur)
    cur = { ...head, dimensions: {}, nodes: {} }
    size = JSON.stringify(cur).length
  }
  const add = (kind: 'dimensions' | 'nodes', key: string, value: unknown) => {
    const cost = JSON.stringify(key).length + JSON.stringify(value).length + 2
    if (size + cost > limit) fresh()
    ;(cur[kind] as Record<string, unknown>)[key] = value
    size += cost
  }
  for (const [k, v] of Object.entries(body.dimensions)) add('dimensions', k, v)
  for (const [k, v] of Object.entries(body.nodes)) add('nodes', k, v)
  fresh()
  return pieces
}

/** Import sources in batches the import door takes in one call (/api/self/import-web: 60 sources, 200,000 characters). */
export function sourceBatches<T extends { content: string }>(sources: T[], maxSources = 60, maxChars = 190_000): T[][] {
  const out: T[][] = []
  let cur: T[] = []
  let chars = 0
  for (const s of sources) {
    if (cur.length && (cur.length >= maxSources || chars + s.content.length > maxChars)) {
      out.push(cur)
      cur = []
      chars = 0
    }
    cur.push(s)
    chars += s.content.length
  }
  if (cur.length) out.push(cur)
  return out
}
